import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Typography as MuiTypography,
  TextField,
  Tooltip,
} from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { endOfDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useTheme as useStyledTheme } from 'styled-components/macro';
import { v4 as uuidv4 } from 'uuid';

import FormToggle from 'components/Form/FormToggle/FormToggle';
import MuiFormAutocomplete from 'components/Form/MuiFormAutocomplete/MuiFormAutocomplete';
import MuiFormDatePicker from 'components/Form/MuiFormDatePicker/MuiFormDatePicker';
import MuiFormInput from 'components/Form/MuiFormInput/MuiFormInput';
import MuiFormSelect from 'components/Form/MuiFormSelect/MuiFormSelect';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { AVATAR_STYLES, getConsistentAvatarBackgroundColor } from 'helpers/avatar';
import {
  anyBulkFormCategoryAllowsAttachments,
  anyBulkRowCategoryAllowsAttachments,
  parseRequestCategoryId,
  requestCategoryQualifiesForCreationAttachments,
} from 'helpers/categoryAllowsAttachments';
import { getInitials } from 'helpers/string';
import { CloseIcon, PlusIcon } from 'icons';

import {
  encodeCombinedCategorySubcategoryValue,
  parseCombinedCategorySubcategoryValue,
} from './categorySubcategoryOptions';
import NewTicketCreationAttachmentsSection from './components/NewTicketCreationAttachmentsSection/NewTicketCreationAttachmentsSection';
import type { NewTicketFormValuesT } from './declarations';
import {
  clearAllBulkCreationAttachmentFormFields,
  clearCreationAttachmentFormFields,
} from './helpers/clearCreationAttachmentFormFields';
import { withLiveCreationAttachmentFields } from './helpers/creationAttachmentFormValues';
import { useOpenNewTicketFormResolver } from './hooks/useOpenNewTicketFormResolver';
import useOpenNewTicketPopup from './hooks/useOpenNewTicketPopup';
import {
  AddCategoryLineBtnStyled,
  CloseButtonStyled,
  FormStyled,
  NewTicketPopupContentWrapStyled,
  NewTicketPopupScrollWrapStyled,
  getPopupContentStyle,
} from './OpenNewTicketPopup.styled';

const OpenNewTicketPopup = (): JSX.Element => {
  const [categoryRows, setCategoryRows] = useState<string[]>([uuidv4()]);
  const [isMultipleTickets, setIsMultipleTickets] = useState(false);
  const [rowCategoryIds, setRowCategoryIds] = useState<{ [key: string]: number | undefined }>({});

  const bulkCategoryIds = useMemo((): number[] => {
    if (!isMultipleTickets) {
      return [];
    }
    const ids = categoryRows
      .map(rowId => rowCategoryIds[rowId])
      .filter((id): id is number => typeof id === 'number' && Number.isFinite(id));
    return Array.from(new Set(ids)).sort((a, b) => a - b);
  }, [categoryRows, isMultipleTickets, rowCategoryIds]);

  /**
   * Hooks
   */
  const {
    claimStatusesOptions,
    roofTopIdsOptions,
    isLoadingRoofTopOptions,
    agentIdsOptions,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions,
    handleCloseNewTicketPopup,
    handleSubmitForm,
    handleAmountBlur,
    isPopupOpened,
    handleRoofTopChange,
    handleAgentChange,
    ticketRequestCategories,
    combinedCategorySubcategoryOptions,
    isLoadingCombinedCategorySubcategoryOptions,
    subcategoriesByCategoryId,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    setAdvisorNumber,
    setRecentRoData,
    checkIfRoExists,
    isCreatingNewTicket,
    isLoadingClaimStatusesOptions,
    isLoadingRequestCategoriesOptions,
    isLoadingAgentsOptions,
    setShowLineNumberDropdown,
    setShowAdvisorNumberDropdown,
    setShowTechNumberDropdown,
    showLineNumberDropdown,
    showAdvisorNumberDropdown,
    showTechNumberDropdown,
    isRoWarningPopupOpen,
    setIsRoWarningPopupOpen,
    lineNumbers,
    techNumbers,
    advisorNumbers,
    setLineNumbers,
    setAdvisorNumbers,
    setTechNumbers,
    setSelectedRoofTopId,
    handleAdvisorNumberChange: _handleAdvisorNumberChange,
    creationAttachmentTypesOptions,
    isLoadingCreationAttachmentTypesOptions,
  } = useOpenNewTicketPopup({
    categoryRows,
    isMultipleTickets,
    bulkCategoryIds,
  });

  /**
   * Hooks
   */
  const { userProfile } = useUserProfile();
  const { roofTopsList, dealerSelectedRooftopIds } = useTickets();

  /** Dealer options: always prefer roofTopsList (which carries oemFamilyId) so the
   *  OEM lookup for subcategories is guaranteed to find the selected dealer. */
  const dealerOptions: MuiFormSelectOptionT[] = useMemo(() => {
    const refinedIds = dealerSelectedRooftopIds
      ? dealerSelectedRooftopIds
          .split(',')
          .map(id => id.trim())
          .filter(Boolean)
      : [];

    if (roofTopsList && roofTopsList.length > 0) {
      const list =
        refinedIds.length > 0 ? roofTopsList.filter(r => refinedIds.includes(r.id)) : roofTopsList;
      return list
        .map(r => ({ value: r.id, label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return [...roofTopIdsOptions].sort((a, b) => a.label.localeCompare(b.label));
  }, [roofTopsList, dealerSelectedRooftopIds, roofTopIdsOptions]);

  const [techNumber, setTechNumber] = useState<string | undefined>(undefined);
  const [, setRowFormValues] = useState<string[]>([uuidv4()]);
  const [categoryTextValues, setCategoryTextValues] = useState<{ [key: string]: string }>({});
  const [singleTicketMessage, setSingleTicketMessage] = useState<string>('');
  const muiTheme = useTheme();
  const styledTheme = useStyledTheme();
  const isDarkMode = muiTheme.palette.mode === 'dark';
  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useResponsive();

  const agentIdsOptionsWithAvatar = useMemo(
    (): MuiFormSelectOptionT[] =>
      agentIdsOptions.map(
        (o): MuiFormSelectOptionT => ({
          value: o.value,
          label: o.label,
          icon: (
            <Tooltip title={o.label || 'Unknown User'} arrow>
              <Avatar
                sx={{
                  ...AVATAR_STYLES.size,
                  fontSize: AVATAR_STYLES.fontSize,
                  backgroundColor: getConsistentAvatarBackgroundColor(String(o.value), isDarkMode),
                }}
              >
                {getInitials(o.label || 'Unknown User')}
              </Avatar>
            </Tooltip>
          ),
        })
      ),
    [agentIdsOptions, isDarkMode]
  );

  const dynamicZodResolver = useOpenNewTicketFormResolver({
    categoryRows,
    isMultipleTickets,
    ticketRequestCategories,
  });

  const fromMethods = useForm<NewTicketFormValuesT>({
    defaultValues: {
      lineNumber: null,
      amount: '0.00',
      text: '',
      createMultipleTickets: false,
      subCategoryId: null,
      categorySubcategorySelection: '',
      creationAttachmentNote: '',
      creationAttachmentTypeCode: '',
    },
    resolver: dynamicZodResolver,
    mode: 'onChange',
  });
  const [searchParams] = useSearchParams();

  /**
   * Variables
   */
  const { handleSubmit, setValue, reset, getValues, unregister, control, formState } = fromMethods;
  const datePickerMaxDate = endOfDay(new Date());
  const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId);

  const watchedRoofTopId = useWatch({ control, name: 'roofTopId', defaultValue: '' });
  const watchedAgentId = useWatch({ control, name: 'agentId', defaultValue: '' });
  const watchedRequestCategoryId = useWatch({
    control,
    name: 'requestCategoryId',
    defaultValue: undefined,
  });
  const singleCategoryAllowsAttachments = useMemo(
    () =>
      requestCategoryQualifiesForCreationAttachments(
        ticketRequestCategories,
        parseRequestCategoryId(watchedRequestCategoryId)
      ),
    [ticketRequestCategories, watchedRequestCategoryId]
  );

  const watchedFormValues = useWatch<NewTicketFormValuesT>({ control });

  const bulkFormAllowsAttachments = useMemo((): boolean => {
    if (!isMultipleTickets) {
      return false;
    }
    return anyBulkFormCategoryAllowsAttachments(
      ticketRequestCategories,
      categoryRows,
      (watchedFormValues ?? {}) as Record<string, unknown>
    );
  }, [isMultipleTickets, categoryRows, watchedFormValues, ticketRequestCategories]);

  const watchedLineNumber = useWatch({ control, name: 'lineNumber', defaultValue: null });
  const watchedRoNumber = useWatch({ control, name: 'roNumber', defaultValue: '' });
  const prevWatchedRoofTopIdRef = useRef<string | undefined>(undefined);

  useEffect((): void => {
    if (watchedRoofTopId && typeof watchedRoofTopId === 'string') {
      const isRoofTopChange =
        prevWatchedRoofTopIdRef.current !== undefined &&
        prevWatchedRoofTopIdRef.current !== watchedRoofTopId;
      prevWatchedRoofTopIdRef.current = watchedRoofTopId;

      handleRoofTopChange(watchedRoofTopId);

      if (isRoofTopChange) {
        setValue('requestCategoryId' as string, undefined);
        setValue('requestSubcategoryId', null);
        setValue('requestSubCategoryName', null);
        setValue('categorySubcategorySelection', '');
        setValue('text', '');
        setValue('agentId', '');
        setSingleTicketMessage('');

        const freshRow = uuidv4();
        setCategoryRows([freshRow]);
        setCategoryTextValues({});
        setRowCategoryIds({});
        clearCreationAttachmentFormFields(setValue, null);
      }
    }
  }, [watchedRoofTopId, handleRoofTopChange, setValue]);

  useEffect((): void => {
    if (watchedAgentId && typeof watchedAgentId === 'string') {
      handleAgentChange(watchedAgentId);
    }
  }, [watchedAgentId, handleAgentChange]);

  useEffect((): void => {
    if (watchedLineNumber != null) {
      const lineVal =
        typeof watchedLineNumber === 'object' &&
        watchedLineNumber !== null &&
        'value' in watchedLineNumber
          ? (watchedLineNumber as { value: string }).value
          : String(watchedLineNumber);
      handleLineNumberChange(lineVal);
      const selectedLineItem = lineItemsObj.find((item): boolean => item.lineNumber === lineVal);
      setTechNumber(selectedLineItem?.techNumber);
    }
  }, [watchedLineNumber, handleLineNumberChange, lineItemsObj]);

  useEffect((): void => {
    if (watchedRoNumber != null && watchedRoNumber !== '') {
      handleROChange(String(watchedRoNumber));
    }
  }, [watchedRoNumber, handleROChange]);

  const ticketPrioritiesOptionsWithIcon: MuiFormSelectOptionT[] = useMemo(() => {
    const labelToIconAndColor: Record<
      string,
      { icon: React.ReactNode; sxProps: { color: string } }
    > = {
      Critical: { icon: <KeyboardDoubleArrowUp key="critical" />, sxProps: { color: red[900] } },
      High: { icon: <KeyboardArrowUp key="high" />, sxProps: { color: red[500] } },
      Medium: { icon: <KeyboardArrowDown key="medium" />, sxProps: { color: orange[500] } },
      Low: { icon: <KeyboardDoubleArrowDown key="low" />, sxProps: { color: green[500] } },
    };
    return ticketPrioritiesOptions.map(opt => {
      const mapped = labelToIconAndColor[opt.label];
      return {
        value: opt.value,
        label: opt.label,
        icon: mapped?.icon ?? opt.label,
        sxProps: mapped?.sxProps,
      };
    });
  }, [ticketPrioritiesOptions]);

  /**
   * Effects
   */
  useEffect((): void => {
    if (isPopupOpened && getValues('roofTopId') === undefined && selectedRoofTopId !== null) {
      setValue('roofTopId', selectedRoofTopId);
      setSelectedRoofTopId(selectedRoofTopId);
    }
  }, [isPopupOpened, selectedRoofTopId, getValues, setValue, setSelectedRoofTopId]);

  useEffect((): void => {
    if ((isPopupOpened && getValues('agentId') === undefined) || getValues('agentId') === '') {
      if (userProfile && agentIdsOptions.length > 0) {
        const { userId } = userProfile;
        // Only pre-fill if the current user is in the loaded agent list
        const userInOptions = agentIdsOptions.some(o => o.value === userId);
        if (userInOptions) {
          setValue('agentId', userId);
        }
      }
    }
  }, [isPopupOpened, userProfile, getValues, setValue, agentIdsOptions]);

  useEffect((): void => {
    if (isPopupOpened && getValues('ticketPriorityId') === undefined) {
      setValue('ticketPriorityId', 3);
    }
  }, [isPopupOpened, getValues, setValue]);

  useEffect((): void => {
    setValue('techNumber', techNumber);
  }, [techNumber, setValue]);

  useEffect((): void => {
    if (isPopupOpened && roofTopIdsOptions.length === 1) {
      const roofTopId = roofTopIdsOptions[0].value as string;
      setSelectedRoofTopId(roofTopId);
      setValue('roofTopId', roofTopId);
    }
    if (isPopupOpened && agentIdsOptions.length === 1) {
      const agentId = agentIdsOptions[0].value as string;
      setValue('agentId', agentId);
    }
    if (isPopupOpened && lineNumbers.length === 1) {
      const lineNumber = lineNumbers[0].value as string;
      setValue('lineNumber', showLineNumberDropdown ? lineNumber : null);
      setValue('techNumber', lineItemsObj[0]?.techNumber);
    }
  }, [
    roofTopIdsOptions,
    isPopupOpened,
    setSelectedRoofTopId,
    setValue,
    handleRoofTopChange,
    lineItemsObj,
    lineNumbers,
    showLineNumberDropdown,
    handleLineNumberChange,
    agentIdsOptions,
  ]);

  useEffect(
    (): (() => void) => (): void => {
      if (isPopupOpened) {
        reset();
        setCategoryTextValues({});
        setSingleTicketMessage('');
        setCategoryRows([uuidv4()]);
        if (!selectedRoofTopId) {
          setSelectedRoofTopId(undefined);
          setValue('agentId', '');
        }
      }
    },
    [isPopupOpened, reset, selectedRoofTopId, setSelectedRoofTopId, setValue]
  );

  useEffect(() => {
    if (!isPopupOpened) {
      prevWatchedRoofTopIdRef.current = undefined;
      setIsRoWarningPopupOpen(false);
      setShowLineNumberDropdown(false);
      setShowAdvisorNumberDropdown(false);
      setShowTechNumberDropdown(false);
      setAdvisorNumbers([]);
      setTechNumbers([]);
      setTechNumber('');
      setLineNumbers([]);
      setIsMultipleTickets(false);
      setAdvisorNumber(undefined);
      setRecentRoData({
        roofTopId: null,
        roNumber: null,
        agentId: null,
      });
      if (!selectedRoofTopId) {
        setValue('agentId', '');
        setSelectedRoofTopId(undefined);
      }
    }
  }, [
    isPopupOpened,
    selectedRoofTopId,
    setAdvisorNumber,
    setIsRoWarningPopupOpen,
    setLineNumbers,
    setRecentRoData,
    setShowLineNumberDropdown,
    setAdvisorNumbers,
    setTechNumbers,
    setShowAdvisorNumberDropdown,
    setShowTechNumberDropdown,
    setIsMultipleTickets,
    setSelectedRoofTopId,
    setValue,
  ]);

  /**
   * Handlers
   */
  const handleRoNumberBlur = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const roNumber = event.target.value;
    const { roofTopId } = getValues();
    handleROChange(roNumber);
    checkIfRoExists(roofTopId, roNumber, setValue);
  };
  const onAddCategoryClickHandle = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    const newCategoryRow: string = uuidv4();
    setCategoryRows([...categoryRows, newCategoryRow]);
    setCategoryTextValues(prevState => ({
      ...prevState,
      [newCategoryRow]: '', // Initialize the text value for the new category
    }));
  };

  const onRemoveCategoryClickHandle = (id: string): void => {
    const updatedCategoryRows = categoryRows.filter(row => row !== id);
    setCategoryRows(updatedCategoryRows);

    setRowFormValues((prevState): string[] => prevState.filter((row): boolean => row !== id));
    setRowCategoryIds(prevState => {
      const next = { ...prevState };
      delete next[id];
      return next;
    });
    unregister(`requestCategoryId-${id}` as never);
    unregister(`requestSubcategoryId-${id}` as never);
  };

  const handleChangeMultipleTickets = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    setIsMultipleTickets(checked);
    setValue('createMultipleTickets', checked);
    if (checked) {
      clearCreationAttachmentFormFields(setValue, null);
    }
  };

  type CategorySubcategoryFieldKeysT = {
    categoryFieldKey: string;
    subcategoryFieldKey: string;
    subcategoryNameFieldKey: string;
    textFieldKey: string;
    combinedSelectionFieldKey: string;
    categoryTextValuesKey?: string;
  };

  const applyCategorySubcategoryToForm = (
    categoryId: number,
    subcategoryId: number,
    options: CategorySubcategoryFieldKeysT
  ): void => {
    const {
      categoryFieldKey,
      subcategoryFieldKey,
      subcategoryNameFieldKey,
      textFieldKey,
      combinedSelectionFieldKey,
      categoryTextValuesKey,
    } = options;

    setValue(categoryFieldKey, categoryId);
    setValue(subcategoryFieldKey, subcategoryId);
    setValue(
      combinedSelectionFieldKey,
      encodeCombinedCategorySubcategoryValue(categoryId, subcategoryId)
    );

    const selectedCategory = ticketRequestCategories?.find(
      (category): boolean => category.id === categoryId
    );
    const selectedSubcategory = subcategoriesByCategoryId[categoryId]?.find(
      (sub): boolean => sub.id === subcategoryId
    );

    setValue(subcategoryNameFieldKey, selectedSubcategory?.name ?? null);

    const message = selectedSubcategory?.description || selectedCategory?.defaultMessage || '';
    setValue(textFieldKey, message);

    if (categoryTextValuesKey) {
      setCategoryTextValues(
        (prevState): Record<string, string> => ({
          ...prevState,
          [categoryTextValuesKey]: message,
        })
      );

      const resultId = categoryTextValuesKey.split('-').slice(1).join('-');
      const nextRowCategoryIds = { ...rowCategoryIds, [resultId]: categoryId };
      setRowCategoryIds(nextRowCategoryIds);
      if (
        !anyBulkRowCategoryAllowsAttachments(
          ticketRequestCategories,
          categoryRows,
          nextRowCategoryIds
        )
      ) {
        clearAllBulkCreationAttachmentFormFields(setValue, categoryRows);
      }
    } else {
      setSingleTicketMessage(message);
      if (!requestCategoryQualifiesForCreationAttachments(ticketRequestCategories, categoryId)) {
        clearCreationAttachmentFormFields(setValue, null);
      }
    }
  };

  const handleCombinedCategorySubcategoryChange = (
    value: string | number,
    options: CategorySubcategoryFieldKeysT
  ): void => {
    const parsed = parseCombinedCategorySubcategoryValue(value);
    if (!parsed) {
      setValue(options.categoryFieldKey, undefined);
      setValue(options.subcategoryFieldKey, null);
      setValue(options.subcategoryNameFieldKey, null);
      setValue(options.combinedSelectionFieldKey, '');

      if (options.categoryTextValuesKey) {
        const resultId = options.categoryTextValuesKey.split('-').slice(1).join('-');
        const nextRowCategoryIds = { ...rowCategoryIds, [resultId]: undefined };
        setRowCategoryIds(nextRowCategoryIds);
        if (
          !anyBulkRowCategoryAllowsAttachments(
            ticketRequestCategories,
            categoryRows,
            nextRowCategoryIds
          )
        ) {
          clearAllBulkCreationAttachmentFormFields(setValue, categoryRows);
        }
      } else {
        clearCreationAttachmentFormFields(setValue, null);
      }
      return;
    }

    applyCategorySubcategoryToForm(parsed.categoryId, parsed.subcategoryId, options);
  };

  const handleChangeCategorySingle = (): void => {
    const textFieldValue = getValues('text');

    if (!textFieldValue || textFieldValue === '') {
      const categoryId = getValues('requestCategoryId');
      const foundCategory = ticketRequestCategories?.find(
        (category): boolean => category.id === categoryId
      );
      setSingleTicketMessage(foundCategory?.defaultMessage || '');
    }
  };

  return (
    <Popup
      showCloseBtn
      modalTitle="New Ticket"
      popupId={ScrollLockIdE.OpenNewTicketPopup}
      contentStyle={getPopupContentStyle(
        styledTheme,
        isMobile,
        isTablet,
        isDesktop,
        isLargeDesktop
      )}
      isOpen={isPopupOpened}
      onClose={handleCloseNewTicketPopup}
      closeOnEscape={false}
      closeOnDocumentClick={!isRoWarningPopupOpen}
      width={463}
      height={720}
      validationErrorCount={Object.keys(formState.errors).length}
    >
      <NewTicketPopupContentWrapStyled direction="column" align="stretch">
        <FormProvider {...fromMethods}>
          <FormStyled
            role="form"
            onSubmit={handleSubmit(async (data: NewTicketFormValuesT): Promise<void> => {
              await handleSubmitForm(withLiveCreationAttachmentFields(data, getValues));
            })}
          >
            <NewTicketPopupScrollWrapStyled>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Dealer */}
                <MuiTypography variant="subtitle2" fontWeight={700}>
                  Dealer
                </MuiTypography>
                <MuiFormSelect
                  required
                  searchable
                  label="Dealer"
                  name="roofTopId"
                  placeholder=""
                  options={dealerOptions}
                  isLoading={isLoadingRoofTopOptions}
                />

                {/* RO Info */}
                <MuiTypography variant="subtitle2" fontWeight={700}>
                  RO Info
                </MuiTypography>
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormInput
                      required
                      label="RO Number"
                      name="roNumber"
                      placeholder="RO #"
                      onBlur={handleRoNumberBlur}
                    />
                  </Box>
                  <Box flex="1">
                    {showLineNumberDropdown ? (
                      <MuiFormSelect
                        label="Line Number"
                        name="lineNumber"
                        placeholder="Line #"
                        options={lineNumbers}
                      />
                    ) : (
                      <MuiFormInput label="Line Number" name="lineNumber" placeholder="Line #" />
                    )}
                  </Box>
                </Box>

                {/* Open RO Date and Amount */}
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormDatePicker
                      required
                      label="Open RO Date"
                      name="roDate"
                      placeholder="Select date"
                      maxDate={datePickerMaxDate}
                    />
                  </Box>
                  <Box flex="1">
                    <MuiFormInput
                      label="Amount"
                      name="amount"
                      placeholder="0.00"
                      type="number"
                      onBlur={handleAmountBlur(setValue)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                {/* Advisor, Tech and VIN */}
                <Box display="flex" gap={2}>
                  <Box flex="1" onContextMenu={(e): void => e.stopPropagation()}>
                    {showAdvisorNumberDropdown ? (
                      <MuiFormAutocomplete
                        required
                        label="Advisor"
                        placeholder="Select or enter advisor"
                        name="advisorNumber"
                        options={advisorNumbers}
                        allowCustomInput
                      />
                    ) : (
                      <MuiFormInput
                        required
                        label="Advisor"
                        placeholder="Enter advisor"
                        name="advisorNumber"
                      />
                    )}
                  </Box>
                  <Box flex="1" onContextMenu={(e): void => e.stopPropagation()}>
                    {showTechNumberDropdown ? (
                      <MuiFormAutocomplete
                        required
                        label="Tech"
                        name="techNumber"
                        placeholder="Select or enter tech"
                        options={techNumbers}
                        allowCustomInput
                      />
                    ) : (
                      <MuiFormInput
                        required
                        label="Tech"
                        name="techNumber"
                        placeholder="Enter tech"
                      />
                    )}
                  </Box>
                  <Box flex="1" onContextMenu={(e): void => e.stopPropagation()}>
                    <MuiFormInput label="VIN" placeholder="VIN" name="vin" />
                  </Box>
                </Box>

                {/* Claim Status */}
                <MuiTypography variant="subtitle2" fontWeight={700}>
                  Claim Status
                </MuiTypography>
                <Box display="flex" gap={2}>
                  <Box flex="1">
                    <MuiFormSelect
                      required
                      label="Claim Status"
                      name="claimStatusId"
                      placeholder=""
                      options={claimStatusesOptions}
                      isLoading={isLoadingClaimStatusesOptions}
                    />
                  </Box>
                  <Box flex="1">
                    <MuiFormDatePicker
                      label="Claim Submitted Date"
                      name="claimSubmittedDate"
                      placeholder=""
                      maxDate={datePickerMaxDate}
                    />
                  </Box>
                </Box>

                {/* Ticket Setup */}
                <MuiTypography variant="subtitle2" fontWeight={700}>
                  Ticket Setup
                </MuiTypography>
                <Box display="flex" gap={2} alignItems="flex-start">
                  <Box flex={1}>
                    <Tooltip title={!watchedRoofTopId ? 'Select a dealer first' : ''}>
                      <Box component="span" sx={{ display: 'inline-block', width: '100%' }}>
                        <MuiFormSelect
                          required
                          label="Assigned Agent"
                          name="agentId"
                          placeholder=""
                          options={agentIdsOptionsWithAvatar}
                          isLoading={isLoadingAgentsOptions}
                          disabled={!watchedRoofTopId}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box
                    flex={1}
                    display="flex"
                    gap={2}
                    alignItems="flex-start"
                    flexWrap="nowrap"
                    minWidth={0}
                  >
                    <Box flex={1} minWidth={0}>
                      <MuiFormSelect
                        required
                        label="Priority"
                        name="ticketPriorityId"
                        placeholder=""
                        options={ticketPrioritiesOptionsWithIcon}
                        isLoading={isLoadingTicketPrioritiesOptions}
                        renderSelectedValue={(opt): React.ReactNode => (
                          <Box display="flex" alignItems="center" sx={opt.sxProps ?? undefined}>
                            {opt.icon}
                          </Box>
                        )}
                        sx={{
                          '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '14px !important',
                            paddingTop: '6px !important',
                            paddingBottom: '6px !important',
                            display: 'flex',
                            alignItems: 'center',
                          },
                          '& .MuiOutlinedInput-root': { height: 'auto' },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        }}
                      />
                    </Box>
                    <Box
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      flexShrink={0}
                      sx={{ mt: '8px' }}
                    >
                      <Controller
                        name="createMultipleTickets"
                        control={fromMethods.control}
                        render={({
                          field: { ref: _ref, onChange, ...fieldProps },
                        }): JSX.Element => (
                          <>
                            <FormToggle
                              {...fieldProps}
                              name="createMultipleTickets"
                              checked={isMultipleTickets}
                              onChange={(e): void => {
                                onChange(e);
                                handleChangeMultipleTickets(e);
                              }}
                              value={isMultipleTickets.toString()}
                            />
                            <MuiTypography
                              sx={{
                                whiteSpace: 'nowrap',
                                fontSize: '12px',
                                color: 'text.primary',
                                fontWeight: 700,
                              }}
                            >
                              Multiple
                            </MuiTypography>
                            <Tooltip
                              title="Create multiple tickets for this repair order."
                              arrow
                              placement="top"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 16,
                                  color: 'action.active',
                                  cursor: 'help',
                                  ml: 0.5,
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Category — multiple tickets: rows (category / subcategory / message), then add line, then shared RO attachments */}
                {isMultipleTickets ? (
                  <>
                    {categoryRows.map((categoryRow): JSX.Element => {
                      const requestCategoryIdKey = `requestCategoryId-${categoryRow}`;
                      const textKey = `text-${categoryRow}`;
                      const combinedSelectionKey = `categorySubcategorySelection-${categoryRow}`;
                      return (
                        <Box
                          key={`category-row-${categoryRow}`}
                          id={`id-${categoryRow}`}
                          display="flex"
                          flexDirection="column"
                          gap={2}
                          position="relative"
                        >
                          <Tooltip title={!watchedRoofTopId ? 'Select a dealer first' : ''}>
                            <Box component="span" sx={{ display: 'inline-block', width: '100%' }}>
                              <MuiFormSelect
                                required
                                label="Category"
                                name={combinedSelectionKey as never}
                                placeholder=""
                                options={combinedCategorySubcategoryOptions}
                                isLoading={
                                  isLoadingRequestCategoriesOptions ||
                                  isLoadingCombinedCategorySubcategoryOptions
                                }
                                disabled={!watchedRoofTopId}
                                searchable
                                onChange={(value): void => {
                                  handleCombinedCategorySubcategoryChange(value, {
                                    categoryFieldKey: requestCategoryIdKey,
                                    subcategoryFieldKey: `requestSubcategoryId-${categoryRow}`,
                                    subcategoryNameFieldKey: `requestSubCategoryName-${categoryRow}`,
                                    textFieldKey: textKey,
                                    combinedSelectionFieldKey: combinedSelectionKey,
                                    categoryTextValuesKey: requestCategoryIdKey,
                                  });
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Controller
                            name={textKey as never}
                            control={fromMethods.control}
                            render={({ field }): JSX.Element => {
                              const value = field.value;
                              const messageValue = categoryTextValues[requestCategoryIdKey] || '';
                              return (
                                <TextField
                                  {...field}
                                  multiline
                                  minRows={1}
                                  placeholder="Enter message"
                                  fullWidth
                                  size="small"
                                  value={value ?? messageValue ?? ''}
                                  onChange={(e): void => {
                                    field.onChange(e);
                                    setCategoryTextValues(
                                      (prevState): Record<string, string> => ({
                                        ...prevState,
                                        [requestCategoryIdKey]: e.target.value,
                                      })
                                    );
                                  }}
                                  sx={{
                                    '& .MuiInputBase-input': {
                                      fontSize: '13px',
                                      overflow: 'hidden',
                                      resize: 'none',
                                    },
                                    '& .MuiFormHelperText-root': { fontSize: '11px' },
                                    '& .MuiInputBase-root': {
                                      paddingTop: '8px',
                                      alignItems: 'flex-start',
                                    },
                                  }}
                                />
                              );
                            }}
                          />
                          {categoryRows.length > 1 && (
                            <Box display="flex" justifyContent="flex-end">
                              <CloseButtonStyled
                                key={`closeButton-${categoryRow}`}
                                type="button"
                                variant="transparent"
                                size="xs"
                                icon={<CloseIcon />}
                                onClick={(): void => onRemoveCategoryClickHandle(categoryRow)}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    <AddCategoryLineBtnStyled
                      type="button"
                      size="xl"
                      variant="outline"
                      icon={<PlusIcon />}
                      onClick={onAddCategoryClickHandle}
                      children={undefined}
                    />
                    <NewTicketCreationAttachmentsSection
                      key="creation-attachments-bulk-shared"
                      rowIdSuffix={null}
                      allowsAttachments={bulkFormAllowsAttachments}
                      creationAttachmentTypesOptions={creationAttachmentTypesOptions}
                      isLoadingCreationAttachmentTypesOptions={
                        isLoadingCreationAttachmentTypesOptions
                      }
                    />
                  </>
                ) : (
                  <>
                    <Tooltip title={!watchedRoofTopId ? 'Select a dealer first' : ''}>
                      <Box component="span" sx={{ display: 'inline-block', width: '100%' }}>
                        <MuiFormSelect
                          required
                          label="Category"
                          name="categorySubcategorySelection"
                          placeholder=""
                          options={combinedCategorySubcategoryOptions}
                          isLoading={
                            isLoadingRequestCategoriesOptions ||
                            isLoadingCombinedCategorySubcategoryOptions
                          }
                          disabled={!watchedRoofTopId}
                          searchable
                          onChange={(value): void => {
                            handleCombinedCategorySubcategoryChange(value, {
                              categoryFieldKey: 'requestCategoryId',
                              subcategoryFieldKey: 'requestSubcategoryId',
                              subcategoryNameFieldKey: 'requestSubCategoryName',
                              textFieldKey: 'text',
                              combinedSelectionFieldKey: 'categorySubcategorySelection',
                            });
                          }}
                        />
                      </Box>
                    </Tooltip>

                    {/* Message */}
                    <Controller
                      name="text"
                      control={fromMethods.control}
                      render={({ field }): JSX.Element => (
                        <TextField
                          {...field}
                          multiline
                          minRows={1}
                          placeholder="Enter message"
                          fullWidth
                          size="small"
                          value={field.value ?? singleTicketMessage ?? ''}
                          onChange={(e): void => {
                            handleChangeCategorySingle();
                            setSingleTicketMessage(e.target.value);
                            field.onChange(e);
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '13px',
                              overflow: 'hidden',
                              resize: 'none',
                            },
                            '& .MuiFormHelperText-root': { fontSize: '11px' },
                            '& .MuiInputBase-root': {
                              paddingTop: '8px',
                              alignItems: 'flex-start',
                            },
                          }}
                        />
                      )}
                    />

                    <NewTicketCreationAttachmentsSection
                      rowIdSuffix={null}
                      allowsAttachments={singleCategoryAllowsAttachments}
                      creationAttachmentTypesOptions={creationAttachmentTypesOptions}
                      isLoadingCreationAttachmentTypesOptions={
                        isLoadingCreationAttachmentTypesOptions
                      }
                    />
                  </>
                )}
              </Box>
            </NewTicketPopupScrollWrapStyled>
            <Box
              display="flex"
              justifyContent="flex-end"
              mt={-2}
              px={2}
              pt={1}
              pb={2}
              flexShrink={0}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isCreatingNewTicket}
                startIcon={isCreatingNewTicket ? <CircularProgress size={16} /> : null}
              >
                Create
              </Button>
            </Box>
          </FormStyled>
        </FormProvider>
      </NewTicketPopupContentWrapStyled>
    </Popup>
  );
};

export default OpenNewTicketPopup;
